import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from "@angular/router";
import { Observable } from "rxjs";
import { AuthService } from "./auth.service";
import { NavigationService } from "./navigation.service";
import { NotificationService } from "./notification.service";

/**
 * Blocks company-paid end users from the pricing/checkout surface: /pricing,
 * the legacy /plans, and the two routes a plan change funnels through —
 * /new-plan/:id (plan/add-on confirmation) and /payment-details/:id (the
 * Stripe card + "Due today" confirm screen). Their subscription is managed
 * by the company account, so self-service plan changes don't apply to them
 * anywhere in that flow — sends them to Settings instead, mirroring the same
 * `isCompanyEndUser` eligibility check that already hides the FlyShare
 * Rewards card and the Upgrade/Edit Plan CTAs on that page (see
 * AuthService.isCompanyEndUser).
 *
 * /new-plan/:id and /payment-details/:id were added after QA (PR #511,
 * finding S-16) found a company-paid seat could bypass the /pricing block
 * by deep-linking straight to /new-plan/Starter and reach a real Stripe
 * checkout screen — the guard on /pricing alone doesn't protect routes
 * reachable independently of it.
 *
 * A user whose company account expires (company_id cleared / dropped to
 * freemium) regains access automatically — this reads live `user_details`,
 * there's nothing to invalidate.
 */
@Injectable({
  providedIn: "root",
})
export class PricingEligibilityGuardService {
  constructor(
    private readonly authService: AuthService,
    private readonly nav: NavigationService,
    private readonly notification: NotificationService
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (this.authService.isCompanyEndUser) {
      this.notification.toast(
        "Your plan is managed by your company. Contact your admin to make changes.",
        "warning"
      );
      this.nav.navigateTo(["/settings"]);
      return false;
    }

    return true;
  }
}
